import { useEffect, useState } from "react";
import { absoluteUrl, type Route } from "../route";

/**
 * Copy a shareable link to whatever is currently on screen.
 *
 * `navigator.clipboard` needs a secure context — fine on the deployed origin and
 * on localhost, but it is also absent in some embedded browsers, and it rejects
 * when the document is not focused. Both failures are silent, so fall back to
 * showing the URL rather than leaving a button that looks like it worked.
 */
export function CopyLink({ route, label = "Copy link" }: {
  route: Partial<Route>;
  label?: string;
}) {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  const url = absoluteUrl(route);

  // Reset when the target changes, so the tick never refers to a stale link.
  useEffect(() => setState("idle"), [url]);

  useEffect(() => {
    if (state !== "copied") return;
    const id = setTimeout(() => setState("idle"), 2000);
    return () => clearTimeout(id);
  }, [state]);

  async function copy() {
    try {
      // Race a timeout. `writeText` REJECTS when permission is refused, but it
      // can simply never settle when the document is not focused — observed in
      // an automated browser, and the same thing happens if the click lands
      // while focus is elsewhere. An unsettled promise leaves the button
      // looking inert, which reads as "the feature is broken"; a visible
      // fallback with the URL in it does not.
      await Promise.race([
        navigator.clipboard.writeText(url),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("clipboard timeout")), 1500),
        ),
      ]);
      setState("copied");
    } catch {
      setState("failed");
    }
  }

  return (
    <>
      <button
        className="ghost small"
        onClick={() => void copy()}
        title={url}
      >
        {state === "copied" ? "Copied ✓" : label}
      </button>
      {state === "failed" && (
        <span className="muted small">
          {" "}
          Copy failed — the link is <code>{url}</code>
        </span>
      )}
    </>
  );
}
